import { FC, MouseEvent, useRef, useState } from 'react'
import { Dropdown } from 'primereact/dropdown'
import { Button } from 'primereact/button'
import { Toast } from 'primereact/toast'
import { confirmPopup } from 'primereact/confirmpopup'
import { Spinner } from '../../components'
import {
  useClearableBranchesQuery,
  useClearBranchStockMutation,
  useUnclearBranchStockMutation,
} from '../../generated/graphql'
import { showToast } from '../../helpers/showSuccessToast'

const ClearStock: FC = () => {
  const toastSuccess = useRef<Toast>(null)
  const toastError = useRef<Toast>(null)
  const [branchId, setBranchId] = useState<number | null>(null)

  const { loading, data } = useClearableBranchesQuery()
  const [clearBranchStock, { loading: clearing }] = useClearBranchStockMutation()
  const [unclearBranchStock, { loading: unclearing }] =
    useUnclearBranchStockMutation()

  const branchName = data?.clearableBranches.find((b) => b.id === branchId)?.name

  const runClear = async () => {
    try {
      const { data } = await clearBranchStock({ variables: { branchId: branchId! } })
      const result = data?.clearBranchStock
      if (result?.errors?.length) {
        showToast(toastError, result.errors[0].message!, 'error')
        return
      }
      showToast(
        toastSuccess,
        `Cleared ${result?.count ?? 0} stock ${result?.count === 1 ? 'batch' : 'batches'} from ${branchName}`,
      )
    } catch (error) {
      showToast(toastError, 'Server Error', 'error')
      console.log(error)
    }
  }

  const runUnclear = async () => {
    try {
      const { data } = await unclearBranchStock({ variables: { branchId: branchId! } })
      const result = data?.unclearBranchStock
      if (result?.errors?.length) {
        showToast(toastError, result.errors[0].message!, 'error')
        return
      }
      showToast(
        toastSuccess,
        `Restored ${result?.count ?? 0} stock ${result?.count === 1 ? 'batch' : 'batches'} to ${branchName}`,
      )
    } catch (error) {
      showToast(toastError, 'Server Error', 'error')
      console.log(error)
    }
  }

  const confirmClear = (e: MouseEvent<HTMLButtonElement>) => {
    confirmPopup({
      target: e.currentTarget,
      message: `Clear all stock from ${branchName}? Inventory reports will read empty. This can be undone.`,
      icon: 'pi pi-exclamation-triangle',
      accept: runClear,
      reject: () => false,
    })
  }

  if (loading) return <Spinner />

  return (
    <>
      <Toast ref={toastSuccess} />
      <Toast ref={toastError} />
      <div style={{ padding: '1.3rem 3rem 3rem 3rem', color: '#f4f4fc' }}>
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontWeight: 100, fontSize: '1.8rem', color: '#f4f4fc' }}>Clear Stock</h1>
        </div>
        <div
          style={{
            background: 'rgba(45, 45, 51, 0.45)',
            backdropFilter: 'blur(40px)',
            border: '1px solid rgba(134, 134, 134, 0.4)',
            borderRadius: '8px',
            padding: '2rem',
            display: 'inline-flex',
            flexDirection: 'column',
            gap: '1.5rem',
            minWidth: '320px',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            <label htmlFor="branch" style={{ color: '#f4f4fc', fontSize: '1rem' }}>
              Branch
            </label>
            <Dropdown
              id="branch"
              value={branchId}
              options={data?.clearableBranches.map((b) => ({
                label: b.name,
                value: b.id,
              }))}
              onChange={(e) => setBranchId(e.value)}
              placeholder="Select a branch"
              style={{ minWidth: '260px' }}
            />
          </div>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <Button
              label="Clear stock"
              icon="pi pi-trash"
              className="p-button-danger"
              disabled={!branchId || clearing}
              onClick={confirmClear}
            />
            <Button
              label="Undo"
              icon="pi pi-undo"
              className="p-button-secondary p-button-outlined"
              disabled={!branchId || unclearing}
              onClick={runUnclear}
            />
          </div>
          <small style={{ color: '#a0a0a0', maxWidth: '320px' }}>
            Clearing empties the branch's inventory reports (stock value, availability,
            variance, stock-in invoices). Sales, profit, and supplier records are kept.
            Use Undo to bring the stock back.
          </small>
        </div>
      </div>
    </>
  )
}

export { ClearStock }
